import { useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import { AiFillSchedule } from "react-icons/ai";
import { FaCalendarAlt, FaChevronDown, FaChevronRight, FaCog, FaFileContract, FaLayerGroup, FaPen, FaStamp, FaTachometerAlt, FaUsers, FaWallet } from 'react-icons/fa';
import { FaMoneyBillWave, FaMoneyCheckDollar, FaPersonWalkingDashedLineArrowRight, FaUserGroup } from "react-icons/fa6";
import { IoMdClose } from 'react-icons/io';
import { IoDocuments } from "react-icons/io5";
import { LuHistory } from "react-icons/lu";
import { MdBook, MdStars, MdTableChart } from "react-icons/md";

const ALL_NAV_CONFIG = {
  "Manage Queue": { name: "Queue", icon: <FaPersonWalkingDashedLineArrowRight />, path: "/admin/manage-queue" },
  "Manage Applications": { 
    name: 'Applications', icon: <IoDocuments />,
    children: [
      { name: 'Document Requirements', icon: <FaUsers />, path: '/admin/manage-review-applicants', permissionKey: "Document Requirements" }, 
      { name: 'Exam Schedule Requests', icon: <FaUsers />, path: '/admin/manage-exam-and-interview-schedule', permissionKey: "Exam Schedule Requests" }, 
      { name: 'Exam Results', icon: <FaUsers />, path: '/admin/manage-exam-and-interview-results', permissionKey: "Exam Results" }, 
      { name: 'Academic Period', icon: <FaUsers />, path: '/admin/manage-enrollment-period', permissionKey: "Enrollment Period" }, 
      { name: 'Applications', icon: <FaUsers />, path: '/user-admin/applications', permissionKey: "Applications" }, 
    ]
  },
  "Manage Accounts": { // Parent Permission
    name: 'User Management', icon: <FaUsers />,
    children: [
      { name: 'Accounts', icon: <FaUsers />, path: '/admin/manage-accounts' },
      { name: 'Faculty Profile', icon: <FaUsers />, path: '/user-admin/faculty-profile' },
    ]
  },
  "Manage Student Records": { name: 'Student Records', icon: <FaLayerGroup />, path: "/admin/manage-students" },
  "Manage Enrollment": { name: 'Enrollment', icon: <FaStamp />, path: "/admin/manage-enrollment" },
  "Manage Schedule": { name: 'Schedule', icon: <AiFillSchedule />, path: "/user-admin/schedule" },
  "Manage Program": { // Parent Permission
    name: 'Academic Structure', icon: <MdTableChart />,
    children: [
      { name: 'Strands', icon: <MdStars />, path: '/admin/manage-strands', permissionKey: "Strands" }, // Match backend
      { name: 'Sections', icon: <FaUserGroup />, path: '/admin/manage-sections', permissionKey: "Sections" }, // Match backend
      { name: 'Subjects', icon: <MdBook />, path: '/admin/manage-subjects', permissionKey: "Subjects" }, // Match backend
    ]
  },
  "Manage Payments": { // Parent Permission
    name: 'Payments', icon: <FaMoneyCheckDollar />,
    children: [
      { name: 'Process Payments', icon: <FaMoneyBillWave />, path: '/user-admin/payments', permissionKey: "Process Payments" }, // Match backend
      { name: 'Tuition Details', icon: <FaMoneyBillWave />, path: '/admin/manage-tuition-details', permissionKey: "Fee Structure" }, // Match backend
      { name: 'User Ledger', icon: <FaWallet />, path: '/admin/manage-user-ledger', permissionKey: "User Ledger" },     // Match backend
      { name: 'Payment History', icon: <LuHistory />, path: '/admin/payment-history', permissionKey: "Payment History" }, // Match backend
    ]
  },
  "Create Announcements": { name: 'Announcements', icon: <FaPen />, path: "/admin/create-announcements" },
  "System Logs": { // Permission String (must match backend, e.g., "Overall System Logs" or "System Logs")
    name: 'System Logs', icon: <FaFileContract />, path: "/admin/manage-system-logs"
  },
};

const staticTopItems = [
  { name: 'Dashboard', icon: <FaTachometerAlt />, path: '/user-admin/dashboard' },
];

const SideNav = ({ isOpen, onClose, isSmallScreen }) => {
  const [openSubmenusByClick, setOpenSubmenusByClick] = useState({});
  const [hoveredItemName, setHoveredItemName] = useState(null);
  const location = useLocation();
  const [authorizedModules, setAuthorizedModules] = useState([]);
  const [isLoadingModules, setIsLoadingModules] = useState(true);
  const [moduleError, setModuleError] = useState(null);

  useEffect(() => {
    const fetchUserModulesForNav = async () => {
      const userId = localStorage.getItem('id');
      const userRole = localStorage.getItem('role');
      if (!userId) {
        setIsLoadingModules(false); setAuthorizedModules([]); setModuleError('User session not found.'); return;
      }
      setIsLoadingModules(true); setModuleError(null);
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/accounts/${userId}`);
        if (!response.ok) { const e = await response.text(); throw new Error(`Account fetch failed: ${response.status} - ${e}`);}
        const accountData = await response.json();
        if (!accountData.data) throw new Error('Invalid account data structure');
        const { hasCustomAccess, customModules, role: accountRoleFromDB } = accountData.data;
        let modulesToShow = [];
        if (hasCustomAccess) {
          modulesToShow = customModules || [];
        } else {
          const roleToUseForDefaults = accountRoleFromDB || userRole;
          if (!roleToUseForDefaults) throw new Error("User role undefined for default modules.");
          const roleResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/roles/${encodeURIComponent(roleToUseForDefaults)}`);
          if (!roleResponse.ok) { const e = await roleResponse.text(); throw new Error(`Role fetch failed: ${roleResponse.status} - ${e}`);}
          const roleData = await roleResponse.json();
          if (roleData.data?.modules) modulesToShow = roleData.data.modules;
        }
        setAuthorizedModules(modulesToShow);
      } catch (err) {
        console.error('SideNav: Error fetching modules:', err);
        setModuleError(err.message); setAuthorizedModules([]);
      } finally {
        setIsLoadingModules(false);
      }
    };
    fetchUserModulesForNav();
  }, []);

  const finalNavItems = useMemo(() => {
    const dynamicNavItems = [];
    const authorizedSet = new Set(authorizedModules || []);


    Object.entries(ALL_NAV_CONFIG).forEach(([parentPermissionKey, navItemConfig]) => {
      const isParentExplicitlyAuthorized = authorizedSet.has(parentPermissionKey);
      let authorizedChildrenOfThisParent = [];

      if (navItemConfig.children && navItemConfig.children.length > 0) {
        authorizedChildrenOfThisParent = navItemConfig.children.filter(childConfig => {
          if (childConfig.permissionKey) {
            return authorizedSet.has(childConfig.permissionKey);
          }
          return isParentExplicitlyAuthorized;
        });

        if (authorizedChildrenOfThisParent.length > 0) {
          dynamicNavItems.push({
            ...navItemConfig,
            children: authorizedChildrenOfThisParent, 
          });
        }
      } else if (isParentExplicitlyAuthorized) {
        dynamicNavItems.push(navItemConfig);
      }
    });
    return [...staticTopItems, ...dynamicNavItems];
  }, [authorizedModules]);

  useEffect(() => {
    let activeParentName = null;
    for (const item of finalNavItems) {
      if (item.children?.some(child => location.pathname.startsWith(child.path))) {
        activeParentName = item.name;
        break;
      }
    }
    if (activeParentName && !openSubmenusByClick[activeParentName]) {
      setOpenSubmenusByClick(prev => ({ ...prev, [activeParentName]: true }));
    }
  }, [location.pathname, finalNavItems]); // Removed openSubmenusByClick from deps here

  const handleSubmenuToggleOnClick = (itemName) => {
    setOpenSubmenusByClick(prev => ({
      ...Object.fromEntries(Object.keys(prev).map(key => [key, false])),
      [itemName]: !prev[itemName],
    }));
    setHoveredItemName(null);
  };

  const handleMouseEnterItem = (itemName) => {
    if (!isSmallScreen && !isOpen) {
      setHoveredItemName(itemName);
    }
  };

  const handleMouseLeaveItem = () => {
    if (!isSmallScreen && !isOpen) {
      setHoveredItemName(null);
    }
  };
  
  const isParentActive = (item) => item.children?.some(child => location.pathname.startsWith(child.path));

  if (isLoadingModules) { /* ... loading UI ... */ }
  if (moduleError) { /* ... error UI ... */ }

  return (
    <>
      <div className={`sidenav ${isOpen ? 'open' : ''}`}>
        <div className="sidenav-header">
          <span className="sidenav-title">MENU</span>
          {isOpen && isSmallScreen && <IoMdClose className="sidenav-close-icon" onClick={onClose} />}
        </div>
        <ul className="sidenav-list">
          {finalNavItems.map(item => {
            const isClickedOpen = openSubmenusByClick[item.name];
            const isHoverOpen = hoveredItemName === item.name && !isSmallScreen && !isOpen;
            const isSubmenuEffectivelyOpen = isClickedOpen || isHoverOpen;

            return (
              <li
                key={item.name}
                className={`sidenav-item ${item.children?.length > 0 ? 'has-submenu' : ''} ${isSubmenuEffectivelyOpen ? 'submenu-open-li' : ''}`}
                onMouseEnter={item.children?.length > 0 ? () => handleMouseEnterItem(item.name) : undefined}
                onMouseLeave={item.children?.length > 0 ? handleMouseLeaveItem : undefined}
              >
                {item.children?.length > 0 ? (
                  <div
                    className={`sidenav-link parent-link ${isParentActive(item) && !isSubmenuEffectivelyOpen ? 'parent-active-collapsed' : ''} ${isSubmenuEffectivelyOpen ? 'parent-open' : ''}`}
                    onClick={() => handleSubmenuToggleOnClick(item.name)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleSubmenuToggleOnClick(item.name); }}
                  >
                    {item.icon}
                    <span className="sidenav-link-text">{item.name}</span>
                    {isSubmenuEffectivelyOpen ? <FaChevronDown className="submenu-arrow" /> : <FaChevronRight className="submenu-arrow" />}
                  </div>
                ) : (
                  <NavLink
                    to={item.path}
                    className={({ isActive }) => `sidenav-link ${isActive ? 'active' : ''}`}
                    onClick={() => {
                        onClose();
                        setHoveredItemName(null);
                    }}
                  >
                    {item.icon}
                    <span className="sidenav-link-text">{item.name}</span>
                  </NavLink>
                )}
                {item.children?.length > 0 && isSubmenuEffectivelyOpen && (
                  <ul className={`sidenav-submenu open`}>
                    {item.children.map(child => ( // item.children here is already the filtered authorized list
                      <li key={child.name} className="sidenav-submenu-item">
                        <NavLink
                          to={child.path}
                          className={({ isActive }) => `sidenav-submenu-link ${isActive ? 'active' : ''}`}
                          onClick={() => {
                            onClose();
                            setHoveredItemName(null);
                          }}
                        >
                          {child.icon && <span className="submenu-item-icon">{child.icon}</span>}
                          <span className="sidenav-link-text">{child.name}</span>
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      </div>
      {isOpen && isSmallScreen && (
        <div className="sidenav-overlay visible" onClick={onClose} />
      )}
    </>
  );
};

export default SideNav;